import { ListingCardInterests, PopularItemCardInterests } from 'app/components/listing-card-interests';
import { Column, Row } from 'app/design/primitives/box';
import { Text } from 'app/design/primitives/text';

import type { PopularItemsByTag } from 'app/network/category';
import type { ClosestListingsByTag, Tag } from 'app/network/listing';
import type { PopularItem } from 'app/types/category';
import type { Listing } from 'app/types/listing';
import { SharerType } from 'app/types/preferences';
import React, { useRef, useState, type Dispatch, type SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, ScrollView, View } from 'react-native';
import { LoadingAvatar } from './interests-loading-avatar';
import { createTheme as createDSTheme } from 'app/shopify';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
const theme = createDSTheme();

const GAP = 12;

const SCROLL_EDGE_POS = 2;

function TagCarouselRow({
  title,
  cardWidth,
  children,
}: {
  title: string;
  cardWidth: number;
  children: React.ReactNode;
}) {
  const scrollRef = useRef<ScrollView>(null);
  const offsetRef = useRef(0);
  const [contentWidth, setContentWidth] = useState(0);
  const [layoutWidth, setLayoutWidth] = useState(0);
  const [scrollX, setScrollX] = useState(0);

  const maxScrollX = Math.max(0, contentWidth - layoutWidth);
  const canScrollLeft = scrollX > SCROLL_EDGE_POS;
  const canScrollRight = scrollX < maxScrollX - SCROLL_EDGE_POS;

  const scrollBy = (dir: -1 | 1) => {
    const currentOffset = offsetRef.current;
    const rawOffset = dir === 1 ? currentOffset + cardWidth + GAP : currentOffset - cardWidth - GAP;
    const nextOffset = Math.min(Math.max(0, rawOffset), maxScrollX);
    scrollRef.current?.scrollTo({ x: nextOffset, animated: true });
    offsetRef.current = nextOffset;
    setScrollX(nextOffset);
  };

  const commitScrollOffset = (
    event: Parameters<NonNullable<React.ComponentProps<typeof ScrollView>['onScroll']>>[0],
  ) => {
    const nextOffset = Math.min(Math.max(0, event.nativeEvent.contentOffset.x), maxScrollX);
    offsetRef.current = nextOffset;
    setScrollX(nextOffset);
  };

  const showArrows = contentWidth > layoutWidth + 4;

  return (
    <Column
      style={{
        gap: theme.spacing['spacing.general.x2'],
        marginBottom: theme.spacing['spacing.general.x5'],
      }}
    >
      <Row alignItems="center" justifyContent="space-between">
        <Text fontWeight="700" variant="bodyLarge">
          {title}
        </Text>
        {showArrows ? (
          <Row style={{ gap: theme.spacing['spacing.general.x1'] }}>
            <Pressable
              accessibilityState={{ disabled: !canScrollLeft }}
              disabled={!canScrollLeft}
              onPress={() => scrollBy(-1)}
              style={{
                width: 36,
                height: 36,
                borderRadius: 18,
                backgroundColor: '#1F2635',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: canScrollLeft ? 1 : 0.35,
              }}
            >
              <IconChevronLeft strokeWidth={ICON_STROKE_WIDTH} size={22} color="white" />
            </Pressable>
            <Pressable
              accessibilityState={{ disabled: !canScrollRight }}
              disabled={!canScrollRight}
              onPress={() => scrollBy(1)}
              style={{
                width: 36,
                height: 36,
                borderRadius: 18,
                backgroundColor: '#1F2635',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: canScrollRight ? 1 : 0.35,
              }}
            >
              <IconChevronRight strokeWidth={ICON_STROKE_WIDTH} size={22} color="white" />
            </Pressable>
          </Row>
        ) : null}
      </Row>
      <View
        onLayout={(e) => {
          setLayoutWidth(e.nativeEvent.layout.width);
        }}
      >
        <ScrollView
          horizontal
          onContentSizeChange={(w) => {
            setContentWidth(w);
          }}
          onMomentumScrollEnd={commitScrollOffset}
          onScroll={(event) => {
            offsetRef.current = event.nativeEvent.contentOffset.x;
          }}
          onScrollEndDrag={commitScrollOffset}
          ref={scrollRef}
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={{ gap: GAP, paddingRight: theme.spacing['spacing.general.x4'] }}
        >
          {children}
        </ScrollView>
      </View>
    </Column>
  );
}

export interface InterestsWebListingAggregateProps {
  layout: 'small' | 'large';
  allTags: Tag[];
  selectedTags: string[];
  sharerType?: SharerType;
  popularItemsByTag?: PopularItemsByTag[];
  closestOffersByTag?: ClosestListingsByTag[];
  selectedPopularItems: PopularItem[];
  setSelectedPopularItems: Dispatch<SetStateAction<PopularItem[]>>;
  isPending: boolean;
  profilePictureUri: string | number | undefined;
}

export function InterestsWebListingAggregate({
  layout,
  allTags,
  selectedTags,
  sharerType,
  popularItemsByTag,
  closestOffersByTag,
  selectedPopularItems,
  setSelectedPopularItems,
  isPending,
  profilePictureUri,
}: InterestsWebListingAggregateProps) {
  const { t } = useTranslation('interests');
  const cardWidth = layout === 'large' ? 148 : 156;
  const isLender = sharerType === SharerType.lender;

  const aggregateTitle = t(`chooseListing.aggregateTitle.${sharerType ?? 'both'}`);

  const tagLabel = (tagId: string) => {
    const tag = allTags.find((tg) => tg.id === tagId);
    if (!tag) return '';
    return tag.tagName;
  };

  return (
    <Column
      style={{
        gap: theme.spacing['spacing.general.x4'],
        width: '100%',
        paddingTop: theme.spacing['spacing.general.x8'],
      }}
    >
      <Text
        textAlign="center"
        variant="headerXSmall"
        style={{
          fontWeight: '700',
          fontSize: layout === 'large' ? 22 : 18,
          lineHeight: layout === 'large' ? 28 : 24,
          paddingHorizontal: theme.spacing['spacing.general.x4'],
        }}
      >
        {aggregateTitle}
      </Text>

      {isPending ? (
        <View style={{ alignItems: 'center', marginVertical: theme.spacing['spacing.general.x4'] }}>
          <LoadingAvatar uri={profilePictureUri} />
        </View>
      ) : null}

      {selectedTags.map((tagId) => {
        const items = isLender
          ? popularItemsByTag?.find((pit) => pit.tagId === tagId)?.popularItems
          : closestOffersByTag?.find((c) => c.tagId === tagId)?.listings;

        if (!items?.length) {
          return (
            <Column
              key={tagId}
              style={{
                gap: theme.spacing['spacing.general.x1'],
                marginBottom: theme.spacing['spacing.general.x4'],
              }}
            >
              <Text fontWeight="700" variant="bodyLarge">
                {tagLabel(tagId)}
              </Text>
              <Text fontSize={15}>{t('noListingsFound')}</Text>
            </Column>
          );
        }

        const listings = items as Listing[];

        return (
          <TagCarouselRow cardWidth={cardWidth} key={tagId} title={tagLabel(tagId)}>
            {isLender
              ? (items as PopularItem[]).map((item) => (
                  <PopularItemCardInterests
                    fixedCardWidth={cardWidth}
                    key={item.id}
                    popularItem={item}
                    selectedPopularItems={selectedPopularItems}
                    setSelectedPopularItems={setSelectedPopularItems}
                  />
                ))
              : listings.map((listing) => (
                  <ListingCardInterests fixedCardWidth={cardWidth} key={listing.id} listing={listing} />
                ))}
          </TagCarouselRow>
        );
      })}
    </Column>
  );
}
